// Calendar / homework / blocking-window bindings. Mirrors iOS CalendarEndpoints
// + backend /calendar. App-blocking enforcement is iOS-only; on web these are
// viewable/editable schedule + homework entries.
import { api } from "./client";

export type CalendarEntryKind = "block" | "homework" | "task";

export interface CalendarEntry {
  id: string;
  entry_kind: CalendarEntryKind;
  title: string;
  notes: string | null;
  /** "self" = you authored it, "class" = inherited from a clan. */
  source: "self" | "class";
  clan_id: string | null;
  clan_name: string | null;
  created_by_user_id: string;
  days: string[];
  start_time: string | null;
  end_time: string | null;
  on_date: string | null;
  tz: string;
  /**
   * Opaque base64 app-set for a block window (iOS-authored). On web we only
   * surface its presence ("custom apps") and pass it through unchanged on edit;
   * there's no app picker in the browser.
   */
  activity_selection: string | null;
  question_set_ids: string[];
  question_set_titles: string[];
  due_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CalendarEntryCreatePayload {
  entry_kind: CalendarEntryKind;
  title: string;
  notes?: string | null;
  clan_id?: string | null;
  days?: string[];
  start_time?: string | null;
  end_time?: string | null;
  on_date?: string | null;
  tz?: string;
  activity_selection?: string | null;
  question_set_ids?: string[];
  due_at?: string | null;
}

export interface CalendarEntryUpdatePayload {
  title?: string;
  notes?: string | null;
  days?: string[];
  start_time?: string | null;
  end_time?: string | null;
  on_date?: string | null;
  tz?: string;
  /** Pass-through of the iOS-authored app set so web edits never clobber it. */
  activity_selection?: string | null;
  question_set_ids?: string[];
  due_at?: string | null;
}

/** Subscribe URLs for the read-only ICS calendar feed (GET /calendar/feed-url). */
export interface CalendarFeedUrl {
  webcal_url: string;
  https_url: string;
  created_at: string | null;
}

export function listCalendar(): Promise<CalendarEntry[]> {
  return api.get<CalendarEntry[]>("/calendar");
}

export function createCalendarEntry(payload: CalendarEntryCreatePayload): Promise<CalendarEntry> {
  return api.post<CalendarEntry>("/calendar", payload);
}

export function updateCalendarEntry(
  id: string,
  payload: CalendarEntryUpdatePayload,
): Promise<CalendarEntry> {
  return api.patch<CalendarEntry>(`/calendar/${encodeURIComponent(id)}`, payload);
}

export function deleteCalendarEntry(id: string): Promise<void> {
  return api.delete<void>(`/calendar/${encodeURIComponent(id)}`);
}

/** Mint/return the user's ICS subscribe URLs. `rotate` reissues the token. */
export function getCalendarFeedUrl(rotate = false): Promise<CalendarFeedUrl> {
  return api.get<CalendarFeedUrl>(`/calendar/feed-url${rotate ? "?rotate=true" : ""}`);
}
